import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { iConnToolbarColour } from "src/components/shared-components/interfaces/iConnToolbarColour";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AddressService } from "src/service/address.service";
import { AlertService } from "src/service/alert.service";
import { EventService } from "src/service/event.service";
import { LoaderService } from "src/service/loader.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";

@Component({
    selector: "app-address-search",
    templateUrl: "./address-search.component.html",
    styleUrls: ["./address-search.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
/**
 * Modal that lets the user search and pick a datapoint (address).
 *
 * The list is fetched server-side, scoped to the current building/condominium (via {@link idArea}),
 * paginated (`nRow`/`offset`) and requested in the lightweight shape (`getSimpleData=true`): this
 * replaces the previous unbounded `?InstanceGUID=000` call that downloaded every datapoint of every
 * gateway (multi-MB) and could crash the browser.
 *
 * Dismiss contract (relied upon by the weekly-planning form and MUST be preserved): on confirm with a
 * selection it dismisses with `{ selectedAddress }` where `selectedAddress` is the FULL address
 * (hydrated by id so `methods`/`hiLevelDataFormat` are present for the value picker); on cancel or
 * confirm with no selection it dismisses with `{ selectedAddress: null }` (cancel: no data).
 */
export class AddressSearchComponent
  implements OnInit, OnDestroy, iConnToolbarColour, iEvent, iTranslation {
  private _logContext: string = "Address-search.component";

  /** Page size for the paginated fetch (`nRow`). */
  private _numberOfItemsPerPage: number = 100;
  private _currentPage: number = 0;
  private _total: number = 0;

  private _selectedAddress: Address = null;

  private _searchSubject: Subject<string> = new Subject<string>();
  private _searchSubscription: Subscription = null;
  private _networkServiceSubscription: Subscription = null;
  private _addressRequestSubscription: Subscription = null;
  private _translateSubscription: Subscription = null;
  private _infiniteScrollEvent: any = null;

  public _eventSubscriptions: Array<Subscription> = null;

  public toolBarColor: string = "primary";
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  public addresses: Array<Address> = [];
  public searchText: string = "";
  public isLoading: boolean = true;
  public showErrorContent: boolean = false;
  public allLoaded: boolean = false;

  /**
   * Height of one row, used to fill the loading list with just enough skeletons: mirrors
   * `.addr-item` in the SCSS (`--min-height` plus its vertical margins).
   */
  public readonly skeletonRowHeight: number = 76;

  //#region Translated strings
  public lblSearchPlaceholder: string = "";
  public lblNoAddress: string = "";
  //#endregion

  /**
   * Area id of the current building/condominium: the fetch is scoped to it so only this
   * condominium's datapoints are returned. When not provided (`<= 0`) pagination still caps the
   * payload, but the query is not scoped.
   */
  @Input() idArea: number = -1;

  constructor(
    private _modalCtrl: ModalController,
    private _addressService: AddressService,
    private _alertService: AlertService,
    private _networkService: NetworkService,
    private _translateService: TranslateService,
    private _loaderService: LoaderService,
    private _loggerService: LoggerService,
    private _eventService: EventService
  ) {}

  public ngOnInit(): void {
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();
    this.subscribeEvents();
    this.subscribeSearch();
    this.loadAddresses(true);
  }

  public ngOnDestroy(): void {
    this._searchSubscription?.unsubscribe();
    this._networkServiceSubscription?.unsubscribe();
    this._addressRequestSubscription?.unsubscribe();
    this._translateSubscription?.unsubscribe();
    this.unsubscribeEvents();
  }

  //#region Events
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalCtrl.dismiss();
      })
    );
  }

  public unsubscribeEvents(): void {
    for (const sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.searchAddressPlaceholder", "dictionary.noAddressFound"])
      .subscribe((translation) => {
        this.lblSearchPlaceholder =
          translation["dictionary.searchAddressPlaceholder"];
        this.lblNoAddress = translation["dictionary.noAddressFound"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Debounce the searchbar input so a fresh (page-0) fetch is issued only after the user pauses
   * typing, instead of one request per keystroke.
   */
  private subscribeSearch(): void {
    this._searchSubscription = this._searchSubject
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe(() => {
        this.loadAddresses(true);
      });
  }

  /**
   * Handle searchbar input: keep the bound value and schedule a debounced reload.
   * @param event the ion-searchbar input event
   */
  public onSearchInput(event: any): void {
    this.searchText = event?.detail?.value ?? "";
    this._searchSubject.next(this.searchText);
  }

  /**
   * Build the query string for the lightweight/paginated address endpoint.
   * @returns query string (without leading `?`)
   */
  private buildQuery(): string {
    let query: string = "";
    const trimmed: string = this.searchText.trim();

    if (trimmed !== "") {
      query += "name=" + encodeURIComponent(trimmed);
    }
    if (this.idArea > 0) {
      if (query.length > 0) {
        query += "&";
      }
      query +=
        "idArea=" +
        encodeURIComponent(this.idArea) +
        "&includeChildArea=true";
    }
    if (query.length > 0) {
      query += "&";
    }
    query += `nRow=${this._numberOfItemsPerPage}&offset=${
      this._currentPage * this._numberOfItemsPerPage
    }`;

    return query;
  }

  /**
   * Fetch a page of addresses. On `reset` the list/selection/state are cleared and page 0 is
   * requested; otherwise the next page is appended (infinite scroll).
   * @param reset true to start a new search from page 0
   */
  public loadAddresses(reset: boolean): void {
    if (reset) {
      this._currentPage = 0;
      this._selectedAddress = null;
      this.addresses = [];
      this.allLoaded = false;
      this.isLoading = true;
      this.showErrorContent = false;
    }

    this._addressRequestSubscription?.unsubscribe();
    this._addressRequestSubscription = this._addressService
      .getAddressesForSmartComponent(this.buildQuery())
      .subscribe(
        (result: any) => {
          const items: Array<Address> = (result?.items ?? []).map(
            (item: any) => new Address(item)
          );
          this._total = result?.total ?? items.length;
          this.addresses = reset ? items : [...this.addresses, ...items];
          this.allLoaded = this.addresses.length >= this._total;
          this.isLoading = false;
          this.completeInfiniteScroll();
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "loadAddresses()",
            "ERROR IN API CALL: " + error?.message
          );
          this.isLoading = false;
          this.showErrorContent = true;
          this.completeInfiniteScroll();
          this._alertService.presentAlertSomethingWrong();
        }
      );
  }

  /**
   * Load the next page when the user scrolls to the bottom.
   * @param event the ion-infinite-scroll event
   */
  public loadMore(event: any): void {
    if (this.allLoaded) {
      event.target.complete();
      return;
    }
    this._infiniteScrollEvent = event;
    this._currentPage++;
    this.loadAddresses(false);
  }

  /** Complete the pending infinite-scroll event, if any. */
  private completeInfiniteScroll(): void {
    if (this._infiniteScrollEvent) {
      this._infiniteScrollEvent.target.complete();
      this._infiniteScrollEvent = null;
    }
  }

  /** Force a data reload after the "something went wrong" retry. */
  public forceReloadData(): void {
    this.loadAddresses(true);
  }

  /**
   * Mark the tapped address as the (single) current selection.
   * @param address the address to select
   */
  public selectAddress(address: Address): void {
    this._selectedAddress = address;
  }

  /**
   * @param address the address to test
   * @returns true when the given address is the current selection
   */
  public isSelected(address: Address): boolean {
    return (
      this._selectedAddress != null && this._selectedAddress.id === address.id
    );
  }

  /** @returns true when an address is currently selected. */
  public get hasSelection(): boolean {
    return this._selectedAddress != null;
  }

  /** Close the modal without selecting anything. */
  public onClickCancel(): void {
    this._modalCtrl.dismiss();
  }

  /**
   * Confirm the selection. The list rows come from the lightweight payload (which may omit
   * `methods`/`hiLevelDataFormat`), so the full address is re-fetched by id before dismissing to
   * guarantee the caller (value picker) has everything it needs.
   */
  public async onClickConfirm(): Promise<void> {
    if (this._selectedAddress == null) {
      this._modalCtrl.dismiss({ selectedAddress: null });
      return;
    }

    let fullAddress: Address = this._selectedAddress;
    try {
      await this._loaderService.presentLoaderWaiting();
      const result = await this._addressService.getAddressById(
        this._selectedAddress.id
      );
      const hydrated = Array.isArray(result) ? result[0] : result;
      if (hydrated) {
        fullAddress = new Address(hydrated);
      }
    } catch (error) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "onClickConfirm()",
        "Hydration error: " + error?.message
      );
    } finally {
      await this._loaderService.dismissLoaderWaiting();
    }

    this._modalCtrl.dismiss({ selectedAddress: fullAddress });
  }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection
   * changes type/state. A local property is changed to reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;
          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }
}
